import React from 'react';
import { AugmentIds, AUGMENTS } from './catalog';

const AUGMENT_COLOR_STYLES = Object.freeze({
  red: { hex: '#E53935', background: 'rgba(229,57,53,0.14)' },
  orange: { hex: '#FB8C00', background: 'rgba(251,140,0,0.14)' },
  yellow: { hex: '#FFD700', background: 'rgba(255,215,0,0.18)' },
  green: { hex: '#43A047', background: 'rgba(67,160,71,0.14)' },
  brown: { hex: '#8D6E63', background: 'rgba(141,110,99,0.18)' },
  blue: { hex: '#64B5F6', background: 'rgba(100,181,246,0.16)' },
  purple: { hex: '#AB47BC', background: 'rgba(171,71,188,0.16)' },
  cyan: { hex: '#4DD0E1', background: 'rgba(77,208,225,0.16)' },
  gold: { hex: '#FFD700', background: 'rgba(255,215,0,0.18)' },
  slate: { hex: '#8A8A8A', background: 'rgba(138,138,138,0.2)' },
  gray: { hex: '#D0D0D0', background: 'rgba(208,208,208,0.16)' }
});

const getAugmentHex = (colorKey) => (AUGMENT_COLOR_STYLES[colorKey]?.hex || null);
const getAugmentBackground = (colorKey) => (AUGMENT_COLOR_STYLES[colorKey]?.background || null);

function describeAugment(id) {
  switch (id) {
    case AugmentIds.BOUNTY_HUNTER: return 'Whenever this piece captures, gain $1. Max $2 per round.';
    case AugmentIds.PATHFINDER: return 'Whenever this piece moves, gain $1 for every 10 squares it travels this round. Max $3 per round.';
    case AugmentIds.TAXMAN: return 'When this piece first delivers check each round, gain $2.';
    case AugmentIds.EXECUTIONER: return 'When this piece delivers checkmate, gain $8.';
    case AugmentIds.VETERAN: return 'If this piece survives, gain $1.';
    case AugmentIds.JAILER: return 'Whenever this piece captures for the first time each round, add that piece to your bench. Cannot add a King.';
    case AugmentIds.RECRUITER: return 'If this piece survives, add one Pawn to your bench.';
    case AugmentIds.DEMAGOGUE: return 'At the beginning of the round, if you have more pawns than the opponent, gain $2 and add one Pawn to your bench.';
    case AugmentIds.CROWN_TAX: return 'At the beginning of the round, gain $2.';
    case AugmentIds.ROYAL_DECREE: return 'Your rerolls cost $1.';
    default: return '';
  }
}

export function AugmentDock({
  theme,
  inLeftNav = false,
  augShopOffers,
  augmentsGlowActive,
  augDockStaged,
  money,
  rerollCost,
  onDismissGlow,
  onReroll,
  onBuy,
  onCancel,
  onSetCurrentSpare,
  renderDropSlotContent
}) {
  const offers = augShopOffers || [];
  const highlightAugments = augmentsGlowActive;
  const tintColor = (color) => getAugmentHex(color) || '#999';
  const tintBg = (color) => getAugmentBackground(color) || (theme === 'dark' ? 'rgba(80,80,80,0.22)' : 'rgba(231,231,231,0.6)');

  return (
    <div
      data-augdock="1"
      className={highlightAugments ? 'gc-glow-blue' : ''}
      onMouseEnter={onDismissGlow}
      onTouchStart={onDismissGlow}
      style={{
        width: inLeftNav ? 'auto' : 560,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        margin: inLeftNav ? '0' : '8px auto',
        border: '1px solid #555',
        borderRadius: 8,
        padding: 8,
        userSelect: 'none',
        cursor: 'default',
        overflow: 'hidden'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700 }}>Augments</div>
        <div style={{ flex: 1 }} />
        <button
          onClick={onReroll}
          disabled={(money || 0) < rerollCost}
          title={`Reroll ($${rerollCost})`}
          style={{
            padding: '6px 10px',
            border: '1px solid #666',
            borderRadius: 6,
            background: 'transparent',
            color: theme === 'dark' ? '#eee' : '#333',
            fontWeight: 700,
            cursor: (money || 0) < rerollCost ? 'not-allowed' : 'pointer'
          }}
        >
          Reroll ${rerollCost}
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, overflowY: 'auto', paddingRight: 4 }}>
        {offers.map((id, idx) => {
          const aug = AUGMENTS[id];
          if (!aug) {
            const placeholderBorder = theme === 'dark' ? 'rgba(120, 120, 120, 0.6)' : 'rgba(150, 150, 150, 0.6)';
            const placeholderBg = theme === 'dark' ? 'rgba(60, 60, 60, 0.35)' : 'rgba(230, 230, 230, 0.7)';
            const placeholderText = theme === 'dark' ? '#bbb' : '#666';
            return (
              <div
                key={'aug-empty-'+idx}
                data-augslot-index={idx}
                style={{
                  border: `2px solid ${placeholderBorder}`,
                  background: placeholderBg,
                  borderRadius: 8,
                  padding: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: placeholderText,
                  height: 200,
                  boxSizing: 'border-box',
                  flex: '0 0 200px'
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>Augment purchased</div>
                <div style={{ fontSize: 12, textAlign: 'center', maxWidth: '100%' }}>Reroll or advance to refresh offers.</div>
              </div>
            );
          }
          const frame = tintColor(aug.color);
          const affordable = (money || 0) >= (aug.priceGold || 0);
          const staged = (augDockStaged || [])[idx] || null;
          const isActive = !!staged;
          const nameColor = theme === 'dark' ? '#eee' : '#222';
          const restrictionLabel = aug.kingOnly ? '(King Only)' : null;
          return (
            <div key={'aug-'+id+'-'+idx}
                 data-augslot-index={idx}
                 style={{
                   border: `2px solid ${frame}`,
                   background: tintBg(aug.color),
                   borderRadius: 8,
                   padding: 8,
                   display: 'flex',
                   flexDirection: 'column',
                   gap: 8,
                   height: 200,
                   boxSizing: 'border-box',
                   flex: '0 0 200px'
                 }}
                 title={`${aug.name}: ${describeAugment(id)}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, color: nameColor }}>
                  <span>{aug.name}</span>
                  {restrictionLabel ? (
                    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.4px', color: 'rgb(147, 147, 147)' }}>{restrictionLabel}</span>
                  ) : null}
                </div>
                <div style={{ fontWeight: 700, color: affordable ? (theme === 'dark' ? '#eee' : '#333') : '#999' }}>${aug.priceGold}</div>
              </div>
              <div style={{ fontSize: 12, color: theme === 'dark' ? '#ddd' : '#444', marginBottom: 2 }}>{describeAugment(id)}</div>
              <div style={{ display: 'flex', marginTop: 'auto', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 10 }}>
                <div style={{
                      width: 86,
                      height: 86,
                      border: `2px ${isActive ? 'solid' : 'dotted'} ${isActive ? frame : '#c8c8c8'}`,
                      borderRadius: 8,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: theme === 'dark' ? '#161616' : '#fff',
                      position: 'relative'
                    }}
                     onMouseDown={() => onSetCurrentSpare('augment-slot', id, idx)}
                     onTouchStart={() => onSetCurrentSpare('augment-slot', id, idx)}
                >
                  {renderDropSlotContent(staged, { tintHex: frame })}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch', minWidth: 120 }}>
                  <button
                    onClick={() => onBuy(idx)}
                    disabled={!staged || (money || 0) < (aug.priceGold || 0) || (aug.kingOnly && staged.pieceType !== 'wK')}
                    style={{
                      padding: '8px 14px',
                      border: '1px solid #666',
                      borderRadius: 6,
                      background: (!staged || !affordable) ? (theme === 'dark' ? '#3a3a3a' : '#ddd') : (theme === 'dark' ? '#2a2a2a' : '#f7f7f7'),
                      color: (!staged || !affordable) ? '#999' : (theme === 'dark' ? '#eee' : '#333'),
                      fontWeight: 800,
                      cursor: (!staged || !affordable) ? 'not-allowed' : 'pointer',
                      textAlign: 'left'
                    }}
                    title={staged ? 'Buy and equip' : 'Drag a piece into the slot above'}
                  >
                    Buy
                  </button>
                  <button
                    onClick={() => staged && onCancel(idx)}
                    disabled={!staged}
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #666',
                      borderRadius: 6,
                      background: 'transparent',
                      color: theme === 'dark' ? '#eee' : '#333',
                      fontWeight: 700,
                      cursor: staged ? 'pointer' : 'default',
                      visibility: staged ? 'visible' : 'hidden',
                      textAlign: 'left'
                    }}
                    title={staged ? 'Remove staged piece' : 'Drag a piece into the slot above'}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Export color helpers for other components
export { AUGMENT_COLOR_STYLES, getAugmentHex, getAugmentBackground, describeAugment };

